import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function CookieConsent() {
  const { t } = useTranslation("common");
  const [show, setShow] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("openlab-cookies");
    if (!saved) {
      const timer = setTimeout(() => setShow(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(
      "openlab-cookies",
      JSON.stringify({ essential: true, analytics: true, marketing: true, timestamp: Date.now() })
    );
    setShow(false);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem(
      "openlab-cookies",
      JSON.stringify({ essential: true, analytics, marketing, timestamp: Date.now() })
    );
    setShow(false);
  };

  const handleReject = () => {
    localStorage.setItem(
      "openlab-cookies",
      JSON.stringify({ essential: true, analytics: false, marketing: false, timestamp: Date.now() })
    );
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[90] animate-fade-in-up">
      <div className="bg-background-900/95 backdrop-blur-2xl border-t border-primary-400/15 shadow-2xl shadow-primary-500/5">
        <div className="w-full px-4 sm:px-6 lg:px-10 py-5 md:py-6">
          <div className="max-w-7xl mx-auto">
            {!prefsOpen ? (
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-500/10 border border-primary-400/20">
                    <i className="ri-shield-check-line text-xl text-primary-300" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-foreground-100 mb-1">
                      {t("cookies.title")}
                    </h3>
                    <p className="text-[13px] text-foreground-300/70 leading-relaxed max-w-2xl">
                      {t("cookies.description")}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setPrefsOpen(true)}
                    className="px-4 py-2.5 rounded-full text-[13px] font-label font-medium text-foreground-300/70 hover:text-foreground-100 border border-foreground-100/10 hover:border-primary-400/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {t("cookies.preferences")}
                  </button>
                  <button
                    type="button"
                    onClick={handleReject}
                    className="px-4 py-2.5 rounded-full text-[13px] font-label font-medium text-foreground-300/70 hover:text-foreground-100 border border-foreground-100/10 hover:border-primary-400/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {t("cookies.reject")}
                  </button>
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="px-5 py-2.5 rounded-full text-[13px] font-label font-semibold text-foreground-50 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-400 hover:to-primary-500 glow-primary transition-all duration-500 cursor-pointer whitespace-nowrap"
                  >
                    {t("cookies.accept")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-sm text-foreground-100">
                    {t("cookies.prefsTitle")}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setPrefsOpen(false)}
                    className="p-1.5 rounded-lg text-foreground-400/60 hover:text-foreground-200 hover:bg-primary-500/5 transition-all cursor-pointer"
                  >
                    <i className="ri-close-line text-lg" />
                  </button>
                </div>

                <div className="space-y-3">
                  {/* Essential — always on */}
                  <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-background-900/40 border border-foreground-100/5">
                    <div>
                      <p className="text-[13px] font-medium text-foreground-100">{t("cookies.essential")}</p>
                      <p className="text-[12px] text-foreground-400/60">{t("cookies.essentialDesc")}</p>
                    </div>
                    <span className="text-[11px] font-label font-semibold text-primary-400/80 uppercase tracking-wider px-2 py-1 rounded-md bg-primary-500/8">
                      {t("cookies.alwaysOn")}
                    </span>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-background-900/40 border border-foreground-100/5">
                    <div>
                      <p className="text-[13px] font-medium text-foreground-100">{t("cookies.analytics")}</p>
                      <p className="text-[12px] text-foreground-400/60">{t("cookies.analyticsDesc")}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAnalytics(!analytics)}
                      className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer ${analytics ? "bg-primary-500" : "bg-foreground-100/10"}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-foreground-50 shadow-sm transition-all duration-300 ${analytics ? "translate-x-5" : ""}`}
                      />
                    </button>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-background-900/40 border border-foreground-100/5">
                    <div>
                      <p className="text-[13px] font-medium text-foreground-100">{t("cookies.marketing")}</p>
                      <p className="text-[12px] text-foreground-400/60">{t("cookies.marketingDesc")}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setMarketing(!marketing)}
                      className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer ${marketing ? "bg-primary-500" : "bg-foreground-100/10"}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-foreground-50 shadow-sm transition-all duration-300 ${marketing ? "translate-x-5" : ""}`}
                      />
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setPrefsOpen(false)}
                    className="px-4 py-2.5 rounded-full text-[13px] font-label font-medium text-foreground-300/70 hover:text-foreground-100 border border-foreground-100/10 hover:border-primary-400/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {t("cookies.cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={handleAcceptSelected}
                    className="px-5 py-2.5 rounded-full text-[13px] font-label font-semibold text-foreground-50 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-400 hover:to-primary-500 glow-primary transition-all duration-500 cursor-pointer whitespace-nowrap"
                  >
                    {t("cookies.savePrefs")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}